import { LoadingContent } from '../components/feedback';
import { useState } from 'react';
import type { AuditFilter } from '@mcpanel/core';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api/client';
import { ClientPackChecks } from '../components/ClientPackChecks';
import { JobTable } from '../components/JobTable';
import { Empty, ErrorAlert } from '../components/ui';
import { formatDateTime, jobKindLabel, jobStatusInfo, viaLabel } from '../format';
import { usePoll } from '../hooks/usePoll';

export function Jobs() {
  const [params] = useSearchParams();
  const serverId = params.get('serverId') ?? undefined;
  const jobId = params.get('jobId');
  const jobs = usePoll(
    async () => {
      const list = await api.jobs({ limit: 1000, serverId });
      if (!jobId) return list;
      const selected = list.find((job) => job.id === jobId) ?? (await api.job(jobId));
      return [selected, ...list.filter((job) => job.id !== jobId)];
    },
    3000,
    [serverId, jobId],
  );
  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1 data-search-id="jobs">ジョブ</h1>
          <p>起動・停止・作成などの実行状況。行を開くと段階ごとの状況を確認できます。</p>
        </div>
      </div>
      <ClientPackChecks serverId={serverId} />
      <ErrorAlert error={jobs.error} />
      <div className="card">
        <LoadingContent loading={!jobs.data && jobs.loading}>
          <JobTable jobs={jobs.data ?? []} />
        </LoadingContent>
      </div>
    </div>
  );
}

export function Audit() {
  const [filters, setFilters] = useState<AuditFilter>({});
  const audit = usePoll(() => api.audit(200, filters), 10000, [JSON.stringify(filters)]);
  const entries = audit.data ?? [];
  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1 data-search-id="audit">監査ログ</h1>
          <p>誰が・どこから（Web / AI）・何をしたかの記録。</p>
        </div>
      </div>
      <div className="row">
        {(['start', 'end', 'requestedBy', 'via', 'operation', 'target', 'result'] as const).map((key) => (
          <label key={key}>
            {
              {
                start: '開始日',
                end: '終了日',
                requestedBy: '操作者',
                via: '経路',
                operation: '操作',
                target: '対象',
                result: '結果',
              }[key]
            }
            {key === 'via' ? (
              <select
                className="input"
                value={filters.via ?? ''}
                onChange={(e) =>
                  setFilters({ ...filters, via: e.target.value ? (e.target.value as AuditFilter['via']) : undefined })
                }
              >
                <option value="">すべて</option>
                <option value="web">Web</option>
                <option value="mcp">AI</option>
                <option value="system">System</option>
              </select>
            ) : (
              <input
                className="input"
                type={key === 'start' || key === 'end' ? 'date' : 'text'}
                value={filters[key] ?? ''}
                onChange={(e) => setFilters({ ...filters, [key]: e.target.value || undefined })}
              />
            )}
          </label>
        ))}
        <a
          className="btn"
          href={`/api/v1/audit/download?${new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => typeof entry[1] === 'string')).toString()}`}
        >
          ダウンロード（.log）
        </a>
      </div>
      <ErrorAlert error={audit.error} />
      <div className="card">
        <LoadingContent loading={!audit.data && audit.loading}>
          {entries.length === 0 ? (
            <Empty>記録はまだありません。</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>利用者</th>
                    <th>経路</th>
                    <th>操作</th>
                    <th>対象</th>
                    <th>結果</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="small">{formatDateTime(entry.createdAt)}</td>
                      <td>{entry.requestedBy}</td>
                      <td>
                        <span className="tag">{viaLabel[entry.via] ?? entry.via}</span>
                      </td>
                      <td>
                        {entry.operation === 'request_auto_execute'
                          ? 'AI が実行'
                          : entry.operation === 'mcp_policy_change'
                            ? 'AI からの変更の設定を変更'
                            : entry.operation === 'system.updated' && typeof entry.parameters.message === 'string'
                              ? entry.parameters.message
                              : (jobKindLabel[entry.operation] ?? entry.operation)}
                      </td>
                      <td>
                        {entry.target === 'system' ? (
                          'Panel'
                        ) : (
                          <Link to={`/servers/${encodeURIComponent(entry.target)}`}>{entry.target}</Link>
                        )}
                      </td>
                      <td className="small">{jobStatusInfo[entry.result]?.label ?? entry.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </LoadingContent>
      </div>
    </div>
  );
}
